import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import ChartContent, {ChartContentChart} from 'charts/chart-content/ChartContent';
import {ChartNode} from 'charts/ChartTypes';
import {GetChart, GetChartVariables} from 'charts/network/__generated/GetChart';
import {GET_CHART_QUERY} from 'charts/network/GetChart';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {assertPipelineStepRawDataIsKind} from 'pipelines/PipelineTypes';

import getDownloadAsPNGAction from './chart-view/getDownloadAsPNGAction';
import DataViewContext, {OverflowDataViewAction, useDataViewContext} from './DataViewContext';
import DataViewPane from './DataViewPane';
import FailedView from './FailedView';
import LoadingView from './LoadingView';

export interface ChartViewWithChartProps {
  chart: ChartContentChart;
}

export interface ChartViewWithChartIdProps {
  chartId: string;
}

export type ChartViewProps = ChartViewWithChartProps | ChartViewWithChartIdProps | Record<string, never>;

function isChartViewWithChart(props: ChartViewProps): props is ChartViewWithChartProps {
  return props.hasOwnProperty('chart');
}

function isChartViewWithChartId(props: ChartViewProps): props is ChartViewWithChartIdProps {
  return props.hasOwnProperty('chartId');
}

/**
 * Render view for charts.
 *
 * Can be used in three ways:
 * 1. With a `chart` prop: Renders the provided chart object directly.
 * 2. With a `chartId` prop: Queries and renders the chart by ID.
 * 3. With no props: Uses analysisView from context.
 *
 * @example
 * // With chart object
 * <ChartView chart={chartObject} />
 *
 * // With chart ID
 * <ChartView chartId="cht_abc123" />
 *
 * // With analysisView from DataViewContext
 * <ChartView />
 */
export default function ChartView(props: ChartViewProps = {}): JSX.Element {
  if (isChartViewWithChart(props)) {
    return <ChartViewWithChart chart={props.chart} />;
  }

  if (isChartViewWithChartId(props)) {
    return <ChartViewWithChartId chartId={props.chartId} />;
  }

  // Default to analysisView from context
  return <ChartViewWithAnalysisView />;
}

function ChartViewWithChart({chart}: ChartViewWithChartProps): JSX.Element {
  const dataViewContextValue = useDataViewContext();

  const actions: Array<OverflowDataViewAction> = useMemo(() => {
    // Only add download action if the chart has raster fields (full ChartNode)
    if (chart.rasterDownloadUrl != null && chart.rasterExternalFileId != null) {
      return [getDownloadAsPNGAction(chart as ChartNode)];
    }
    return [];
  }, [chart]);

  const newChartViewContextValue = useMemo(
    () => ({
      ...dataViewContextValue,
      isChartView: true,
      actions: {
        ...dataViewContextValue.actions,
        overflowActions: [...dataViewContextValue.actions.overflowActions, ...actions],
      },
      dataTestAttrs: {component: 'ChartView', element: 'root'},
    }),
    [dataViewContextValue, actions]
  );

  return (
    <DataViewContext.Provider value={newChartViewContextValue}>
      <DataViewPane>
        <ChartContent chart={chart} />
      </DataViewPane>
    </DataViewContext.Provider>
  );
}

function ChartViewWithChartId({chartId}: ChartViewWithChartIdProps): JSX.Element {
  return (
    <BenchlingQuery<GetChart, GetChartVariables>
      query={GET_CHART_QUERY}
      renderLoading={() => <LoadingView />}
      variables={{chartId}}
    >
      {(data) =>
        isInaccessible(data.chart) ? (
          <FailedView statusMessage="You do not have access to this chart." />
        ) : (
          <ChartViewWithChart chart={data.chart} />
        )
      }
    </BenchlingQuery>
  );
}

function ChartViewWithAnalysisView(): JSX.Element {
  const dataViewContextValue = useDataViewContext();
  const {analysisView, previewData} = dataViewContextValue;

  assert(
    analysisView != null,
    'analysisView must be provided in context when using ChartViewWithAnalysisView'
  );
  const {lineage} = analysisView;

  const {renderedChart, actions} = useMemo(() => {
    const isPreview = previewData != null;
    assert(
      !isPreview || previewData.previewDraftStepData.chartPreviews.length === 1,
      'Expected one chart preview'
    );

    const stepOutputReference = lineage[lineage.length - 1].stepOutputReference!;
    const rawData = stepOutputReference.outputStepData.rawDatas[stepOutputReference.rawDataIndex!];

    let renderedChart: ChartContentChart;
    const actions: Array<OverflowDataViewAction> = [];
    if (!isPreview) {
      assertPipelineStepRawDataIsKind(rawData, PipelineStepDataKind.CHART);
      renderedChart = rawData;
      actions.push(getDownloadAsPNGAction(rawData));
    } else {
      renderedChart = previewData.previewDraftStepData.chartPreviews[0];
    }

    return {isPreview, chartNode: rawData, renderedChart, actions};
  }, [lineage, previewData]);

  const newDataFrameViewContextValue = useMemo(
    () => ({
      ...dataViewContextValue,
      isChartView: true,
      actions: {
        ...dataViewContextValue.actions,
        overflowActions: [...dataViewContextValue.actions.overflowActions, ...actions],
      },
      dataTestAttrs: {component: 'ChartView', element: 'root'},
    }),
    [dataViewContextValue, actions]
  );

  return (
    <DataViewContext.Provider value={newDataFrameViewContextValue}>
      <DataViewPane>
        <ChartContent chart={renderedChart} />
      </DataViewPane>
    </DataViewContext.Provider>
  );
}
